/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Target } from '@fluentui/react/lib/Callout';
import * as React from 'react';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { BoxRenderer, LineRenderer } from 'src/modules/labeling/components/Renderers/ShapeRenderers';
import { Tooltip } from 'src/modules/labeling/components/Tooltip';
import { MultilineLabel } from 'src/modules/labeling/types/Label';
import { ShapeUIProps } from 'src/modules/labeling/types/ShapeUIProps';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';

export type ShapeProps = ShapeUIProps & {
    /**
     * Whether resizing labels and predictions should be disabled or not.
     */
    disableResize: boolean;

    /**
     * Labeling view options.
     */
    viewOptions: ViewOptions;

    /**
     * Unique identifier for the tokens container.
     */
    containerId: string;

    /**
     * Callback functions that get invoked whenever the shape is clicked.
     */
    onClick: (label: MultilineLabel, target: Target) => void;

    /**
     * Callback that gets invoked whenever a label or prediction gets resized.
     */
    onResize: (label: MultilineLabel, knob: 'start' | 'end') => void;

    /**
     * Whether the shape is hidden or displayed.
     */
    hidden: boolean;

    /**
     * Shape's color.
     */
    color: string;

    /**
     * The tooltip content to display while hovering.
     */
    tooltipContent?: (label: MultilineLabel) => JSX.Element;

    /**
     * Make visual tweaks to the labeler to make
     * it compatible with char tokenization.
     */
    useCharTokenization: boolean;
};

export const Shape = React.memo((props: ShapeProps) => {
    const {
        renderingLevel,
        renderedAs,
        onResize,
        onClick,
        viewOptions,
        disableResize,
        label,
        containerId,
        tooltipContent,
        hidden,
        ...rest
    } = props;
    const ref = React.useRef<HTMLElement>();
    const labelRef = React.useRef(label);

    // Hack to be able to memoize callbacks without depending
    // on the label which gets regenerated each render.
    labelRef.current = label;

    const handleClick = React.useCallback(() => {
        onClick(labelRef.current, ref.current);
    }, [onClick]);

    const handleRenderTooltip = React.useCallback(
        (content: React.ReactNode, target: Target) => (
            <Tooltip label={label} target={target} content={tooltipContent && tooltipContent(labelRef.current)}>
                {content}
            </Tooltip>
        ),
        [tooltipContent]
    );

    const handleResize = React.useCallback((knob: 'start' | 'end') => onResize(labelRef.current, knob), []);

    const actualDisableResize = !isMachineLearned(label.entity.typeId) || disableResize;

    return (
        <span style={{ opacity: hidden ? 0 : 1 }} aria-hidden="true">
            {renderedAs === 'line' ? (
                <LineRenderer
                    disableResize={actualDisableResize}
                    onHandlesKeydown={handleResize}
                    ref={ref}
                    onRenderTooltip={handleRenderTooltip}
                    onClick={handleClick}
                    viewOptions={viewOptions}
                    renderingLevel={renderingLevel}
                    renderedAs={label.viewOptions?.isDashed ? 'dashedLine' : renderedAs}
                    containerId={containerId}
                    {...rest}
                />
            ) : (
                <BoxRenderer
                    disableResize={actualDisableResize}
                    onHandlesKeydown={handleResize}
                    ref={ref}
                    onRenderTooltip={handleRenderTooltip}
                    onClick={handleClick}
                    viewOptions={viewOptions}
                    renderingLevel={renderingLevel}
                    renderedAs={renderedAs}
                    {...rest}
                />
            )}
        </span>
    );
});

Shape.displayName = 'Shape';
